import { FunctionComponent, useEffect, useState, FC } from 'react';
import { Box, CircularProgress, styled, Typography } from '@mui/material';
import { useGetSettlementData, useStartOver } from '../services/flowManager';
import { useGetMerchantConfigurationOptions } from '../services/configManager';
import OrderData from '../components/OrderData';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useFlipScreen } from '../FlipScreenProvider';
import logger from '../services/loggerManager';
import Page from '../components/Page';
import { MainPaperLayout } from '../components/MainPaperLayout';
import useFeedbackSnackbar from '../hooks/useFeedbackSnackbar';
import { SnackbarKey } from 'notistack';
import { useNavigate } from 'react-router-dom';
import {
  ButtonName,
  AppView,
  FlipOrigin,
  CONFIRM_PAGE_TITLES,
} from '../constants';
import { MuiTrackerButton } from '../components/TrackerButtonWrapper';
import { FEEDBACK_TYPE, SnackbarFeedback } from '../services/feedbackManager';

type PageState = 'Processing' | 'ThankYou' | 'OrderSummary' | 'ChargeError';
const Confirm: FunctionComponent = () => {
  const [isOrderSummary, setIsOrderSummary] = useState<boolean>(false);
  const settlementData = useGetSettlementData();

  const [feedback, closeSnackbar] = useFeedbackSnackbar();
  const updateIsOrderSummary = (newValue: boolean) =>
    setIsOrderSummary(newValue);
  const pageState: PageState =
    settlementData.orderId && !isOrderSummary
      ? 'ThankYou'
      : isOrderSummary
      ? 'OrderSummary'
      : !settlementData.orderId && settlementData.receivedData
      ? 'ChargeError'
      : 'Processing';

  return (
    <Page pageName={AppView[pageState]} showBar disableStartOver>
      <MainPaperLayout
        header={CONFIRM_PAGE_TITLES[pageState]}
        content={
          pageState === 'ThankYou' ? (
            <ThankYouContent />
          ) : pageState === 'Processing' ? (
            <ProcessingContent />
          ) : pageState === 'OrderSummary' ? (
            <OrderSummaryContent
              displayError={
                settlementData.shouldSettle &&
                isOrderSummary &&
                !settlementData.lenderReferenceId
              }
              feedback={feedback}
            />
          ) : (
            <ErrorContent feedback={feedback} />
          )
        }
        actionArea={
          <ConfirmButton
            pageState={pageState}
            settlementData={settlementData}
            setIsOrderSummary={updateIsOrderSummary}
            closeFeedback={closeSnackbar}
          />
        }
      />
    </Page>
  );
};
export default Confirm;

const StyledWarningBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  color: theme.palette.primary.main,
  marginLeft: 10,
  fontWeight: 500,
  fontSize: 16,
}));

const StyledLoaderBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 50,
});

type ProcessingContentProps = {};
const ProcessingContent: FC<ProcessingContentProps> = () => {
  return (
    <Box>
      <StyledWarningBox>
        <ErrorOutlineIcon color="primary" />
        <Typography variant="warning">
          Hang tight, this might take a minute
        </Typography>
      </StyledWarningBox>
      <StyledLoaderBox>
        <CircularProgress thickness={4} color="primary" size={100} />
      </StyledLoaderBox>
    </Box>
  );
};

const StyledContentBox = styled(Box)({
  width: '60%',
});

type ThankYouContentProps = {};
const ThankYouContent: FC<ThankYouContentProps> = () => {
  return (
    <StyledContentBox>
      <OrderData />
      <Box display="flex">
        <ErrorOutlineIcon color="primary" />
        <Typography variant="warning">
          Be sure to write down your Order ID and save it for future use. You
          might need to provide this number to the sales representative.
        </Typography>
      </Box>
    </StyledContentBox>
  );
};

type OrderSummaryContentProps = {
  displayError: boolean;
  feedback: ({ type, isPersistent }: SnackbarFeedback) => void;
};
const OrderSummaryContent: FC<OrderSummaryContentProps> = ({
  displayError,
  feedback,
}) => {
  useEffect(() => {
    if (displayError) {
      feedback({
        type: FEEDBACK_TYPE.TRANSACTION_ERROR,
        isPersistent: true,
      });
    }
  }, [displayError]);
  return (
    <>
      <StyledContentBox>
        <OrderData />
      </StyledContentBox>
    </>
  );
};

const StyledErrorBox = styled(Box)({
  display: 'flex',
});

type ErrorContentProps = {
  feedback: ({ type, isPersistent }: SnackbarFeedback) => void;
};
const ErrorContent: FC<ErrorContentProps> = ({ feedback }) => {
  useEffect(() => {
    feedback({
      type: FEEDBACK_TYPE.PROCESS_PAYMENT_ERROR,
      isPersistent: true,
    });
  }, []);

  return (
    <>
      <StyledErrorBox>
        <Typography variant="warning">
          Unfortunately, Something went wrong
        </Typography>
      </StyledErrorBox>
    </>
  );
};

const ConfirmButton: FC<{
  pageState: PageState;
  settlementData: any;
  setIsOrderSummary: (newValue: boolean) => void;
  closeFeedback: (key?: SnackbarKey | undefined) => void;
}> = ({ pageState, setIsOrderSummary, closeFeedback }) => {
  const navigate = useNavigate();
  const startOver = useStartOver();
  const {
    showTransactionId: displayTransactionId,
    flipScreen: displayFlipScreen,
  } = useGetMerchantConfigurationOptions();
  const flipScreen = useFlipScreen();
  return (
    <>
      {pageState !== 'Processing' && (
        <MuiTrackerButton
          buttonName={ButtonName.Confirm}
          data-testid="continue-startover-btn"
          onClick={async () => {
            if (
              !displayFlipScreen ||
              !displayTransactionId ||
              pageState === 'ChargeError' ||
              pageState === 'OrderSummary'
            ) {
              if (pageState !== 'OrderSummary' && pageState !== 'ChargeError') {
                logger.log('Start over button click', {
                  filename: 'confirm.tsx',
                  function: 'Confirm Button',
                });
              }
              closeFeedback();
              startOver();
              navigate('/main');
            } else {
              logger.log('Continue button click', {
                filename: 'confirm.tsx',
                function: 'Confirm Button',
              });
              await flipScreen({
                origin: FlipOrigin.ChargeSuccess,
                toCustomer: false,
                isFinalFlip: true,
              });
              setIsOrderSummary(true);
            }
          }}
          variant="contained"
          color="primary"
          fullWidth
          size="large"
        >
          {pageState === 'OrderSummary' || pageState === 'ChargeError'
            ? 'Close'
            : 'Continue'}
        </MuiTrackerButton>
      )}
    </>
  );
};
